import type {
  BillingScheduleStatus,
  PaginatedData,
  PaymentAttemptStatus,
  PaymentAttemptView,
  PaymentAuthorizationView,
  PaymentReceiptView,
  PaymentTransactionView,
  RepaymentScheduleBillingView,
  RequestOtpResultView,
} from '@solar/contracts';

import { apiClient } from '../../lib/api-client';
import { toQuery } from '../../lib/api-query';

export interface RepaymentListFilters {
  page?: number;
  pageSize?: number;
  status?: BillingScheduleStatus;
  subscriptionId?: string;
}

export interface AdminRepaymentListFilters extends RepaymentListFilters {
  customerId?: string;
}

export interface PaymentAttemptListFilters {
  page?: number;
  pageSize?: number;
  status?: PaymentAttemptStatus;
  scheduleId?: string;
  customerId?: string;
}

export interface PaymentListFilters {
  page?: number;
  pageSize?: number;
  subscriptionId?: string;
  customerId?: string;
}

export type CustomerRepaymentDetailView = RepaymentScheduleBillingView;

export interface SubmitOtpInput {
  otp: string;
}

export interface SubmitOtpResultView {
  authorization: PaymentAuthorizationView;
  paymentAttempt: PaymentAttemptView;
}

export interface SendAuthorizationResultView {
  authorizationSessionId: string;
  linkExpiresAt: string;
}

export interface OverdueSummaryView {
  overdueCount: number;
  awaitingAuthorizationCount: number;
  totalOverdueAmount: string;
  items: RepaymentScheduleBillingView[];
}

export const billingApi = {
  listRepayments(
    filters: RepaymentListFilters = {},
  ): Promise<PaginatedData<RepaymentScheduleBillingView>> {
    return apiClient.get(`/customer/repayments${toQuery({ ...filters })}`);
  },

  getRepayment(scheduleId: string): Promise<CustomerRepaymentDetailView> {
    return apiClient.get(`/customer/repayments/${scheduleId}`);
  },

  getPaymentAuthorization(id: string): Promise<PaymentAuthorizationView> {
    return apiClient.get(`/customer/payment-authorizations/${id}`);
  },

  resolvePaymentAuthorization(token: string): Promise<PaymentAuthorizationView> {
    return apiClient.get(`/customer/payment-authorizations/resolve${toQuery({ token })}`);
  },

  requestOtp(authorizationId: string): Promise<RequestOtpResultView> {
    return apiClient.post(`/customer/payment-authorizations/${authorizationId}/request-otp`);
  },

  resendOtp(authorizationId: string): Promise<RequestOtpResultView> {
    return apiClient.post(`/customer/payment-authorizations/${authorizationId}/resend-otp`);
  },

  submitOtp(authorizationId: string, input: SubmitOtpInput): Promise<SubmitOtpResultView> {
    return apiClient.post(`/customer/payment-authorizations/${authorizationId}/submit-otp`, input);
  },

  listPaymentAttempts(
    filters: PaymentAttemptListFilters = {},
  ): Promise<PaginatedData<PaymentAttemptView>> {
    return apiClient.get(`/customer/payment-attempts${toQuery({ ...filters })}`);
  },

  getPaymentAttempt(id: string): Promise<PaymentAttemptView> {
    return apiClient.get(`/customer/payment-attempts/${id}`);
  },

  listPayments(filters: PaymentListFilters = {}): Promise<PaginatedData<PaymentTransactionView>> {
    return apiClient.get(`/customer/payments${toQuery({ ...filters })}`);
  },

  getPayment(id: string): Promise<PaymentTransactionView> {
    return apiClient.get(`/customer/payments/${id}`);
  },

  getPaymentReceipt(id: string): Promise<PaymentReceiptView> {
    return apiClient.get(`/customer/payments/${id}/receipt`);
  },

  listAdminRepayments(
    filters: AdminRepaymentListFilters = {},
  ): Promise<PaginatedData<RepaymentScheduleBillingView>> {
    return apiClient.get(`/admin/billing/repayments${toQuery({ ...filters })}`);
  },

  getAdminRepayment(scheduleId: string): Promise<RepaymentScheduleBillingView> {
    return apiClient.get(`/admin/billing/repayments/${scheduleId}`);
  },

  listOverdue(): Promise<OverdueSummaryView> {
    return apiClient.get('/admin/billing/overdue');
  },

  sendAuthorization(scheduleId: string): Promise<SendAuthorizationResultView> {
    return apiClient.post(`/admin/billing/repayments/${scheduleId}/send-authorization`);
  },

  listAdminPaymentAttempts(
    filters: PaymentAttemptListFilters = {},
  ): Promise<PaginatedData<PaymentAttemptView>> {
    return apiClient.get(`/admin/payment-attempts${toQuery({ ...filters })}`);
  },

  getAdminPaymentAttempt(id: string): Promise<PaymentAttemptView> {
    return apiClient.get(`/admin/payment-attempts/${id}`);
  },

  checkPaymentAttemptStatus(id: string): Promise<PaymentAttemptView> {
    return apiClient.post(`/admin/payment-attempts/${id}/check-status`);
  },

  listAdminPayments(
    filters: PaymentListFilters = {},
  ): Promise<PaginatedData<PaymentTransactionView>> {
    return apiClient.get(`/admin/payments${toQuery({ ...filters })}`);
  },

  getAdminPayment(id: string): Promise<PaymentTransactionView> {
    return apiClient.get(`/admin/payments/${id}`);
  },

  getAdminPaymentReceipt(id: string): Promise<PaymentReceiptView> {
    return apiClient.get(`/admin/payments/${id}/receipt`);
  },
};
